import { useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/NotificationContext';
import { cognitoApi } from '../../api/cognitoApi';
import { organizationsApi } from '../../api/erpApi';
import WavelengthLogo from '../../components/WavelengthLogo';
import CoolIcon from '../../components/ui/CoolIcon';
import {
    FONT,
    BTN_MD,
    BTN_MD_SECONDARY,
    NUMS,
    Heading,
    Body,
    MediaSlot,
    Seo,
} from '../../components/marketing/MarketingKit';

// ─────────────────────────────────────────────────────────────────────────────
// /signup, the self-serve onboarding wizard: a split screen, question panel on
// the left, a captioned stand-in for the product art on the right, advancing
// through
//   1. workspace name → 2. business type → 3. goals → 4. apps → 5. brand color
//   → 6. create account (Cognito SignUp) → 7. email code (ConfirmSignUp)
//   → "Setting things up…" (creates the organization on AWS, signs in, lands
//      in the app).
// The org record stores name/type/goals/apps/brand color, the same record the
// register app pulls for workspace branding.
//
// This is a public URL, so the chrome follows the marketing design language:
// monochrome, hairline borders that darken on focus, pill buttons at the kit
// heights, and no product screenshot drawn out of divs. Photography does not
// exist yet, so the right panel is a captioned <MediaSlot> naming the shot that
// belongs there.
// ─────────────────────────────────────────────────────────────────────────────

const INPUT = 'w-full rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white px-[18px] py-[15px] text-[15px] text-[#0A0A0A] placeholder-[#A1A1A1] outline-none transition-colors focus:border-[rgba(10,10,10,0.35)]';
const NEXT_BTN = BTN_MD + ' disabled:cursor-not-allowed disabled:opacity-40';
const BACK_BTN = BTN_MD_SECONDARY;
// Selectable card: hairline that darkens on hover, solid ink border when chosen.
const CHOICE = 'rounded-[16px] border text-left transition-colors';
const CHOICE_ON = 'border-[#0A0A0A] bg-[#FAFAFA] text-[#0A0A0A]';
const CHOICE_OFF = 'border-[rgba(10,10,10,0.10)] bg-white text-[#3D3D3D] hover:border-[rgba(10,10,10,0.30)]';
const TICK_ON = 'border-[#0A0A0A] bg-[#0A0A0A]';
const TICK_OFF = 'border-[rgba(10,10,10,0.25)] bg-white';

const BUSINESS_TYPES = [
    'Multi-location retail', 'Grocery and specialty food', 'Butcher and production',
    'Prepared food and deli', 'Wholesale and distribution', 'Other',
];

const GOALS = [
    'Track sales and revenue', 'Run registers and kiosks', 'Print scale labels',
    'Inventory and purchasing', 'Reconcile deposits with the bank', 'Wholesale and invoicing',
    'Campaign attribution', 'Staff accountability',
];

// Swatches for the customer's own workspace shade, which their workspace and
// their register screens carry. The public site is monochrome, so the swatches
// offered here are the ink ramp; anything else goes in by hex.
const BRAND_COLORS = ['#0A0A0A', '#111111', '#3D3D3D', '#6B6B6B', '#8A8A8A', '#A1A1A1'];

// The apps a workspace can turn on. `id` is written to the org record, so the
// ids are fixed; `preview` is the art direction for the panel on the right.
const APPS = [
    { id: 'pos', name: 'Point of Sale', tagline: 'Ring up any sale on a register, kiosk or phone.', preview: 'Register mid-sale on a counter kiosk', previewNote: 'The order building on the left, tender and change on the right' },
    { id: 'production', name: 'Production', tagline: 'Weigh, label and trace everything you make.', preview: 'Scale printing a weighed label on the production bench', previewNote: 'Hands on the platter, the label feeding out under it' },
    { id: 'fulfillment', name: 'Order Fulfillment', tagline: 'Pick, pack and hand off every online order.', preview: 'Packing iPad on a bench beside a part-filled tote', previewNote: 'One order ticked line by line as it is picked' },
    { id: 'inventory', name: 'Inventory and Receiving', tagline: 'Purchase orders, deliveries and live stock counts.', preview: 'Receiving iPad signing off a delivery at the back door', previewNote: 'A probe reading on screen, cases stacked behind it' },
    { id: 'delivery', name: 'Delivery', tagline: 'Route drivers and track every drop to the door.', preview: 'Driver map with a run in progress', previewNote: 'The van moving between two stops, the office watching' },
    { id: 'payments', name: 'Payments', tagline: 'Cards, wallets and EBT, settled and reconciled.', preview: 'Settlement batch matched against the day takings', previewNote: 'Two columns tying out, the difference called out beneath' },
];

const STEPS = ['name', 'type', 'goals', 'apps', 'brand', 'account', 'verify'];

const SITE = 'https://wavelengtherp.com';
const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebPage',
            '@id': `${SITE}/signup#webpage`,
            url: `${SITE}/signup`,
            name: 'Create a Wavelength workspace',
            description: 'Set up a Wavelength workspace: name the business, pick the apps you need and create the owner account.',
            isPartOf: { '@id': `${SITE}/#organization` },
            publisher: { '@id': `${SITE}/#organization` },
        },
        {
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/overview` },
                { '@type': 'ListItem', position: 2, name: 'Create a workspace', item: `${SITE}/signup` },
            ],
        },
    ],
};

export default function SignupPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    // Errors are toasts, never inline (owner, 2026-10-20).
    const { notifyError, notifySuccess } = useNotify() || {};

    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [settingUp, setSettingUp] = useState(false);

    // The landing hero can hand off an email (?email=…), prefill the account step.
    const [form, setForm] = useState(() => ({
        workspace: '', type: '', goals: [], apps: ['pos'], brand: BRAND_COLORS[0],
        firstName: '', lastName: '',
        email: new URLSearchParams(location.search).get('email') || '',
        password: '', code: '',
    }));
    const up = (k, v) => setForm(f => ({ ...f, [k]: v }));
    // Which app card is hovered → drives the right-panel caption.
    const [hoverApp, setHoverApp] = useState(null);

    const stepId = STEPS[step];

    // Per-step validity → gates NEXT.
    const valid = useMemo(() => {
        if (stepId === 'name') return form.workspace.trim().length >= 2;
        if (stepId === 'type') return !!form.type;
        if (stepId === 'goals') return form.goals.length > 0;
        if (stepId === 'apps') return form.apps.length > 0;
        if (stepId === 'brand') return /^#[0-9a-fA-F]{6}$/.test(form.brand);
        if (stepId === 'account') return form.firstName.trim() && form.lastName.trim() && /.+@.+\..+/.test(form.email) && form.password.length >= 8;
        if (stepId === 'verify') return /^\d{6}$/.test(form.code.trim());
        return false;
    }, [stepId, form]);

    async function next() {
        if (!valid || busy) return;
        if (stepId === 'account') return createAccount();
        if (stepId === 'verify') return confirmAndLaunch();
        setStep(s => s + 1);
    }
    const back = () => setStep(s => Math.max(0, s - 1));

    // Step 6 → Cognito SignUp (sends the email code).
    async function createAccount() {
        setBusy(true);
        try {
            await cognitoApi.signUp(form.email.trim().toLowerCase(), form.password, form.firstName.trim(), form.lastName.trim());
            setStep(s => s + 1);
        } catch (e) {
            if (e.code?.includes('UsernameExists')) notifyError?.('An account with this email already exists. Try logging in instead.');
            else if (e.code?.includes('InvalidPassword')) notifyError?.('Password needs 8+ characters with an uppercase letter, a lowercase letter and a number.');
            else notifyError?.(e.message || 'Could not create the account.');
        } finally { setBusy(false); }
    }

    // Step 7 → confirm the code, create the workspace on AWS, sign in, enter app.
    async function confirmAndLaunch() {
        setBusy(true);
        const email = form.email.trim().toLowerCase();
        try {
            await cognitoApi.confirmSignUp(email, form.code.trim());
        } catch (e) {
            setBusy(false);
            if (e.code?.includes('CodeMismatch')) { notifyError?.("That code doesn't match. Check the email and try again."); return; }
            if (e.code?.includes('Expired')) { notifyError?.('That code expired. We can send a new one.'); return; }
            if (!e.code?.includes('NotAuthorized')) { notifyError?.(e.message || 'Verification failed.'); return; }
            // NotAuthorized = already confirmed, fall through and continue.
        }
        setSettingUp(true);
        try {
            // Create the workspace (org record), retried; the API can be flaky.
            const orgId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const payload = {
                org_id: orgId,
                name: form.workspace.trim(),
                owner_email: email,
                brand_color: form.brand,
                business_type: form.type,
                goals: form.goals,
                apps: form.apps,
                created_via: 'signup',
            };
            let created = false;
            for (let i = 0; i < 3 && !created; i++) {
                try { await organizationsApi.create(payload); created = true; }
                catch { await new Promise(r => setTimeout(r, 900 * (i + 1))); }
            }
            const res = await login(email, form.password);
            if (!res?.ok) throw new Error(res?.error || 'Sign-in failed. Try logging in.');
            if (created) { try { localStorage.setItem('selectedBusiness', orgId); } catch {} }
            navigate('/', { replace: true });
        } catch (e) {
            setSettingUp(false); setBusy(false);
            notifyError?.(e.message || 'Something went wrong setting up the workspace.');
        }
    }

    async function resend() {
        try { await cognitoApi.resendConfirmationCode(form.email.trim().toLowerCase()); notifySuccess?.('A new code is on its way.'); }
        catch (e) { notifyError?.(e.message || 'Could not resend the code.'); }
    }

    // "Setting things up…" interstitial, full screen while the workspace is built.
    if (settingUp) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-[18px] bg-white" style={{ fontFamily: FONT }}>
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-[rgba(10,10,10,0.15)] border-t-[#0A0A0A]" />
                <span className="text-[15px] text-[#0A0A0A]">Setting things up…</span>
            </div>
        );
    }

    const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); next(); } };

    // The app the right-hand caption is describing: the one under the cursor,
    // else the first one turned on.
    const previewApp = APPS.find(a => a.id === (hoverApp || form.apps[0])) || APPS[0];
    const onApps = stepId === 'apps';

    return (
        <div className="flex min-h-screen bg-white text-[#0A0A0A] antialiased" style={{ fontFamily: FONT }} onKeyDown={onKey}>
            <Seo
                title="Create your workspace"
                description="Create a Wavelength workspace in minutes: name the business, choose the apps you run, set the workspace shade and create the owner account. Keep your register."
                path="/signup"
                keywords="wavelength signup, create workspace, back office signup, multi-location retail software account"
                jsonLd={JSON_LD}
            />

            {/* ── Left: the question panel ── */}
            <div className="relative flex min-h-screen w-full flex-col lg:w-[58%]">
                {/* Top bar */}
                <div className="flex items-center justify-between px-[28px] pt-[24px] lg:px-[48px]">
                    <Link to="/overview" className="flex items-center gap-2 text-[#0A0A0A]">
                        <WavelengthLogo className="h-[18px] w-[18px]" />
                        <span className="text-[15px] font-semibold tracking-[-0.01em]">Wavelength</span>
                    </Link>
                    <div className="flex items-center gap-[18px] text-[14px] font-medium">
                        {stepId === 'account' || stepId === 'verify'
                            ? <Link to="/login" className="text-[#6B6B6B] transition-colors hover:text-[#0A0A0A]">Sign in</Link>
                            : <Link to="/overview" className="text-[#6B6B6B] transition-colors hover:text-[#0A0A0A]">Just looking</Link>}
                    </div>
                </div>

                {/* Question + controls */}
                <div className="flex flex-1 flex-col justify-center px-[28px] py-[40px] lg:px-[48px]">
                    <div className="mx-auto w-full max-w-[560px]">
                        {/* Progress ticks, one per step */}
                        <div className="mb-[26px] flex items-center gap-[6px]">
                            {STEPS.map((s, i) => (
                                <span key={s} className="h-[2px] w-[26px] rounded-full transition-colors" style={{ background: i <= step ? '#0A0A0A' : '#ECECEC' }} />
                            ))}
                        </div>

                        {stepId === 'name' && (
                            <>
                                <Heading as="h1">What is your business called?</Heading>
                                <Body className="mt-[10px]">This becomes your workspace. You can rename it any time.</Body>
                                <input autoFocus className={`${INPUT} mt-[26px]`} placeholder="Your business name" value={form.workspace} onChange={e => up('workspace', e.target.value)} />
                            </>
                        )}

                        {stepId === 'type' && (
                            <>
                                <Heading as="h1">What kind of business are you?</Heading>
                                <Body className="mt-[10px]">We set the dashboard up around how you sell.</Body>
                                <div className="mt-[26px] grid grid-cols-1 gap-[10px] sm:grid-cols-2">
                                    {BUSINESS_TYPES.map(t => (
                                        <button key={t} type="button" onClick={() => up('type', t)}
                                            className={`${CHOICE} px-[16px] py-[14px] text-[14px] ${form.type === t ? CHOICE_ON : CHOICE_OFF}`}>
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {stepId === 'goals' && (
                            <>
                                <Heading as="h1">What do you want Wavelength to do?</Heading>
                                <Body className="mt-[10px]">Pick everything that applies.</Body>
                                <div className="mt-[26px] grid grid-cols-1 gap-[10px] sm:grid-cols-2">
                                    {GOALS.map(g => {
                                        const on = form.goals.includes(g);
                                        return (
                                            <button key={g} type="button" onClick={() => up('goals', on ? form.goals.filter(x => x !== g) : [...form.goals, g])}
                                                className={`${CHOICE} flex items-center gap-[10px] px-[16px] py-[13px] text-[14px] ${on ? CHOICE_ON : CHOICE_OFF}`}>
                                                <span className={`flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[5px] border transition-colors ${on ? TICK_ON : TICK_OFF}`}>
                                                    {on && <CoolIcon name="Check_Big" fit strokeWidth={3.36} width="9" height="7" className="text-white" />}
                                                </span>
                                                {g}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {stepId === 'apps' && (
                            <>
                                <Heading as="h1">Which apps do you want?</Heading>
                                <Body className="mt-[10px]">Turn on the apps you need. You can add the rest later.</Body>
                                <div className="mt-[26px] flex flex-col gap-[10px]">
                                    {APPS.map(a => {
                                        const on = form.apps.includes(a.id);
                                        return (
                                            <button key={a.id} type="button"
                                                onClick={() => up('apps', on ? form.apps.filter(x => x !== a.id) : [...form.apps, a.id])}
                                                onMouseEnter={() => setHoverApp(a.id)} onMouseLeave={() => setHoverApp(null)} onFocus={() => setHoverApp(a.id)}
                                                className={`${CHOICE} flex items-center gap-[14px] px-[16px] py-[14px] ${on ? CHOICE_ON : CHOICE_OFF}`}>
                                                <span className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full transition-colors ${on ? 'bg-[#0A0A0A] text-white' : 'bg-[#F5F5F5] text-[#0A0A0A]'}`}>
                                                    <AppGlyph id={a.id} />
                                                </span>
                                                <span className="flex min-w-0 flex-1 flex-col">
                                                    <span className="text-[14px] font-medium text-[#0A0A0A]">{a.name}</span>
                                                    <span className="truncate text-[13px] text-[#6B6B6B]">{a.tagline}</span>
                                                </span>
                                                <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border transition-colors ${on ? TICK_ON : TICK_OFF}`}>
                                                    {on && <CoolIcon name="Check_Big" fit strokeWidth={3.36} width="10" height="8" className="text-white" />}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {stepId === 'brand' && (
                            <>
                                <Heading as="h1">Pick your brand color.</Heading>
                                <Body className="mt-[10px]">Your workspace and your register screens carry this color. You can change it, and add a logo, later in Workspaces.</Body>
                                <div className="mt-[26px] flex flex-wrap items-center gap-[12px]">
                                    {BRAND_COLORS.map(c => (
                                        <button key={c} type="button" onClick={() => up('brand', c)} aria-label={c}
                                            className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105"
                                            style={{ background: c, boxShadow: form.brand === c ? '0 0 0 2px #fff, 0 0 0 4px #0A0A0A' : 'none' }}>
                                            {form.brand === c && <CoolIcon name="Check_Big" fit strokeWidth={2.59} width="14" height="11" className="text-white" />}
                                        </button>
                                    ))}
                                    <div className="flex items-center gap-[8px] rounded-full border border-[rgba(10,10,10,0.10)] px-[14px] py-[10px] transition-colors focus-within:border-[rgba(10,10,10,0.35)]">
                                        <span className="h-[18px] w-[18px] rounded-full border border-[rgba(10,10,10,0.10)]" style={{ background: /^#[0-9a-fA-F]{6}$/.test(form.brand) ? form.brand : '#fff' }} />
                                        <input value={form.brand} onChange={e => up('brand', e.target.value)} className="w-[84px] bg-transparent font-mono text-[14px] text-[#0A0A0A] outline-none" maxLength={7} />
                                    </div>
                                </div>
                                {/* Live preview, the workspace pill exactly as the sidebar renders it */}
                                <div className="mt-[30px] flex w-max items-center gap-[10px] rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white px-[12px] py-[10px]">
                                    <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[12px] text-[14px] font-semibold text-white" style={{ background: form.brand }}>
                                        {(form.workspace.trim()[0] || 'W').toUpperCase()}
                                    </span>
                                    <span className="flex flex-col leading-tight">
                                        <span className="text-[13px] font-semibold text-[#0A0A0A]">{form.workspace.trim() || 'Your workspace'}</span>
                                        <span className="text-[11px] text-[#A1A1A1]">Owner</span>
                                    </span>
                                </div>
                            </>
                        )}

                        {stepId === 'account' && (
                            <>
                                <Heading as="h1">Create your account.</Heading>
                                <Body className="mt-[10px]">One owner account for <span className="font-medium text-[#0A0A0A]">{form.workspace.trim() || 'your workspace'}</span>.</Body>
                                <div className="mt-[26px] grid grid-cols-1 gap-[12px] sm:grid-cols-2">
                                    <input className={INPUT} placeholder="First name" value={form.firstName} onChange={e => up('firstName', e.target.value)} autoFocus />
                                    <input className={INPUT} placeholder="Last name" value={form.lastName} onChange={e => up('lastName', e.target.value)} />
                                </div>
                                <input className={`${INPUT} mt-[12px]`} type="email" placeholder="name@company.com" value={form.email} onChange={e => up('email', e.target.value)} />
                                <input className={`${INPUT} mt-[12px]`} type="password" placeholder="Password, 8+ characters with mixed case and a number" value={form.password} onChange={e => up('password', e.target.value)} />
                                <p className="mt-[14px] text-[12px] leading-[1.6] text-[#A1A1A1]">
                                    By creating an account you agree to our <Link to="/terms" className="underline decoration-[rgba(10,10,10,0.25)] underline-offset-2 transition-colors hover:text-[#0A0A0A]">Terms of Service</Link> and <Link to="/privacy" className="underline decoration-[rgba(10,10,10,0.25)] underline-offset-2 transition-colors hover:text-[#0A0A0A]">Privacy Notice</Link>.
                                </p>
                            </>
                        )}

                        {stepId === 'verify' && (
                            <>
                                <Heading as="h1">Check your email.</Heading>
                                <Body className="mt-[10px]">We sent a six digit code to <span className="font-medium text-[#0A0A0A]">{form.email}</span>.</Body>
                                <input autoFocus inputMode="numeric" maxLength={6} className={`${INPUT} mt-[26px] w-[220px] text-center text-[22px] font-medium tracking-[0.35em] ${NUMS}`} placeholder="000000" value={form.code} onChange={e => up('code', e.target.value.replace(/[^0-9]/g, ''))} />
                                <button type="button" onClick={resend} className="mt-[14px] block text-[13px] font-medium text-[#0A0A0A] underline decoration-[rgba(10,10,10,0.25)] underline-offset-2 transition-colors hover:decoration-[#0A0A0A]">Resend code</button>
                            </>
                        )}

                    </div>
                </div>

                {/* Bottom bar, BACK / NEXT */}
                <div className="flex items-center justify-between border-t border-[rgba(10,10,10,0.10)] px-[28px] py-[18px] lg:px-[48px]">
                    <div>{step > 0 && <button type="button" onClick={back} className={BACK_BTN}>Back</button>}</div>
                    <button type="button" onClick={next} disabled={!valid || busy} className={NEXT_BTN}>
                        {busy ? 'One sec…' : stepId === 'account' ? 'Continue' : stepId === 'verify' ? 'Finish' : 'Next'}
                    </button>
                </div>
            </div>

            {/* ── Right: a captioned stand-in for the product art. On the apps
                   step the caption follows the app under the cursor. ── */}
            <div className="hidden min-h-screen flex-1 p-3 lg:flex">
                <div className="relative flex w-full flex-col overflow-hidden rounded-[28px] bg-[#111111] text-white">
                    <div className="flex flex-1">
                        <MediaSlot
                            dark
                            kind={onApps ? 'Photo' : 'Video'}
                            label={onApps ? previewApp.preview : 'The workspace on a laptop beside an iPad on the counter'}
                            note={onApps ? previewApp.previewNote : 'One continuous shot, neutral surfaces, the day being closed on screen'}
                            className="!rounded-none h-full"
                        />
                    </div>
                    <div className="border-t border-white/10 px-[40px] py-[36px]">
                        <p className="text-[24px] font-semibold leading-[1.2] tracking-[-0.02em]">Every dollar, from register to bank.</p>
                        <p className="mt-[10px] max-w-[420px] text-[14px] leading-[1.6] text-white/55">
                            Sales, production, inventory, reconciliation and your online store, in one workspace. Keep the register you already run.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Small monochrome glyph per app, in the card tiles.
function AppGlyph({ id }) {
    const name = { pos: 'Monitor', production: 'Ruler', fulfillment: 'Shopping_Bag_01', inventory: 'Archive', delivery: 'Navigation', payments: 'Credit_Card_01' }[id] || 'Monitor';
    return <CoolIcon name={name} size={18} />;
}
